/* Progress — honest mastery telemetry, never activity theater (02 §3.6).
 *
 * Chart rules from 09-brief §5 are binding: thin marks, no dual axes, no donuts
 * for close values, selective labels, <= 4 series, hero numbers set large. The
 * calibration curve renders exactly its three buckets — no smoothing, no
 * interpolation, no invented fourth point.
 *
 * The rhythm chip is deliberately small. Coverage and the calibration readout
 * are the surfaces that matter; streaks are information, not the spine.
 *
 * CR-021 removed the Altitude hero, the mountain silhouette and the camp list.
 * They rendered a metaphor rather than a measurement, and 10.3 of the learner
 * profile warns specifically against exactly that kind of furniture.
 */

import { useEffect, useState } from 'react';
import { DESIGN_TOKENS } from '@ascent/shared';
import { api, ApiFailure, type ProgressResponse } from '../api';
import { isUnitCompleted } from '../sync';

/* Sourced from the shared token, never re-typed. These five hues are the
 * Cluster identity of 09-brief §5; a second copy in a screen is exactly how a
 * palette drifts one hex at a time. */
const CLUSTER_HUE: Record<string, string> = DESIGN_TOKENS.cluster;
const DEFAULT_HUE = DESIGN_TOKENS.cluster.C1;
const LEVEL_NAMES = ['Unseen', 'Studied', 'Practiced', 'Assessed', 'Applied'];

export function ProgressScreen({
  onOpenTopic, onOpenSettings,
}: {
  onOpenTopic: (topicId: string) => void;
  onOpenSettings?: () => void;
}) {
  const [data, setData] = useState<ProgressResponse | null>(null);
  const [ledger, setLedger] = useState<{ total: number; covered: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setData(await api.progress());
        /* Coverage is derived, never stored: the release ships the denominators
         * and each Unit declares what it covers, so the numerator is the union
         * of the items behind the Units actually completed. A release without a
         * ledger simply reports none — better than inventing a number. */
        const atlas = await api.atlas();
        if (atlas.topic_ledger) {
          const items = new Set<string>();
          for (const t of atlas.topics) {
            for (const u of t.units) {
              if (!u.covers?.length) continue;
              if (await isUnitCompleted(u.unit_id)) u.covers.forEach((i) => items.add(i));
            }
          }
          setLedger({ total: atlas.topic_ledger.total, covered: items.size });
        }
      } catch (err) {
        setError(err instanceof ApiFailure
          ? (err.code === 'OFFLINE' ? 'offline' : err.message)
          : 'Something went wrong.');
      }
    })();
  }, []);

  if (error === 'offline') {
    return (
      <main className="screen">
        <p className="eyebrow">Progress</p>
        <h1>Offline</h1>
        <p>Progress is computed on the server. Reconnect to see the current picture.</p>
      </main>
    );
  }
  if (error) {
    return (
      <main className="screen">
        <p className="eyebrow">Progress</p>
        <h1>Could not load progress</h1>
        <p>{error}</p>
      </main>
    );
  }
  if (!data) {
    return (
      <main className="screen">
        <p className="eyebrow">Progress</p>
        <div className="skeleton skeleton--line" />
        <div className="skeleton skeleton--block" />
      </main>
    );
  }

  const oci = data.calibration.oci;

  /* Coverage against the topic ledger. The denominators ship in the release as
   * atlas.topic_ledger; the numerator is what shipped Units declare they cover
   * and the learner has completed. Until the Factory backfills `covers` onto the
   * pre-CR-020 Units, this counts only the build tasks — which is honest, and
   * the reason the figure starts small. */
  const ledgerTotal = ledger?.total ?? 0;
  const covered = ledger?.covered ?? 0;

  return (
    <main className="screen">
      <p className="eyebrow">Progress</p>
      <div className="screen__headrow">
        <h1>Progress</h1>
        {onOpenSettings ? (
          <button type="button" className="gear" onClick={onOpenSettings} aria-label="Settings">
            ⚙
          </button>
        ) : null}
      </div>

      {/* ---- Coverage: one honest figure (CR-021) ------------------------
          Altitude, the mountain silhouette and the camp list lived here. They
          measured a metaphor; this measures the profile. */}
      <div className="card">
        <div className="hero">
          <span className="hero__value">{covered.toLocaleString()}</span>
          <span className="hero__unit">of {ledgerTotal.toLocaleString()} covered</span>
        </div>
        <p className="card__meta">
          {ledgerTotal > 0
            ? `${Math.round((covered / ledgerTotal) * 100)}% of the competency profile, target 75%`
            : 'The release carries no ledger yet'}
        </p>
      </div>

      {/* ---- Mastery ladder --------------------------------------------- */}
      <div className="card">
        <div className="card__title"><span>Mastery</span></div>
        <ul className="levels">
          {data.mastery_totals.by_level.map((count, level) => (
            <li key={level}>
              <span className="levels__name">{LEVEL_NAMES[level]}</span>
              <span className="levels__bar">
                <span
                  className="levels__fill"
                  style={{
                    width: `${data.mastery_totals.by_level.reduce((a, b) => a + b, 0) > 0
                      ? (count / data.mastery_totals.by_level.reduce((a, b) => a + b, 0)) * 100
                      : 0}%`,
                    opacity: 0.25 + level * 0.18,
                  }}
                />
              </span>
              <span className="levels__count">{count}</span>
            </li>
          ))}
        </ul>
        <p className="hint">
          Only Assessed and Applied earn mastery points. Studied and Practiced are
          the road there — they move nothing on their own.
        </p>
      </div>

      {/* ---- Calibration: exactly three buckets ------------------------- */}
      <div className="card">
        <div className="card__title">
          <span>Calibration</span>
          <span className="card__meta">{data.calibration.window_days}-day window</span>
        </div>

        {data.calibration.curve.every((b) => b.n === 0) ? (
          <p style={{ margin: 0 }}>
            No confidence data yet. It starts accumulating with your first Quiz.
          </p>
        ) : (
          <>
            <div className="curve">
              {data.calibration.curve.map((b) => {
                const claimed = b.confidence * 100;
                const actual = b.accuracy === null ? null : b.accuracy * 100;
                return (
                  <div className="curve__col" key={b.confidence}>
                    <div className="curve__bars">
                      <span className="curve__claimed" style={{ height: `${claimed}%` }} />
                      <span
                        className="curve__actual"
                        style={{ height: `${actual ?? 0}%`, opacity: actual === null ? 0.15 : 1 }}
                      />
                    </div>
                    <span className="curve__label">
                      {b.confidence === 0.9 ? 'Sure' : b.confidence === 0.7 ? 'Likely' : 'Guess'}
                    </span>
                    <span className="curve__n">
                      {actual === null ? 'no data' : `${Math.round(actual)}% · n=${b.n}`}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="curve__legend">
              <span className="swatch swatch--claimed" /> claimed
              <span className="swatch swatch--actual" /> actual
            </p>

            <p className="oci">
              {oci.direction === 'overconfident'
                ? `Overconfident by ${oci.value_pp} pp`
                : oci.direction === 'underconfident'
                  ? `Underconfident by ${oci.value_pp} pp`
                  : 'Calibrated'}
            </p>
          </>
        )}

        {data.calibration.domains.length > 0 ? (
          <ul className="domains">
            {data.calibration.domains.map((d) => (
              <li key={d.domain_id} className={d.flagged ? 'domains--flagged' : ''}>
                <span className="domains__id">{d.domain_id}</span>
                <span className="domains__gap">
                  {d.gap_pp > 0 ? '+' : ''}{d.gap_pp} pp
                </span>
                <span className="domains__n">n={d.n}</span>
                {d.flagged ? <span className="badge badge--warn">flagged</span> : null}
              </li>
            ))}
          </ul>
        ) : null}

        <p className="hint">
          A Domain is flagged only above {data.calibration.flag_rule.gap_pp} pp AND
          with at least {data.calibration.flag_rule.min_sample} answers in the window.
          Underconfidence is shown but never flagged.
        </p>
      </div>

      {/* ---- Domain heat ------------------------------------------------- */}
      {data.domain_heat.length > 0 ? (
        <div className="card">
          <div className="card__title"><span>Domain heat</span></div>
          <div className="heat">
            {data.domain_heat.map((d) => (
              <div key={d.domain_id} className="heat__cell" title={`${d.domain_id}: mean level ${d.mean_level}`}>
                <span
                  className="heat__swatch"
                  style={{
                    background: CLUSTER_HUE[d.cluster_id] ?? DEFAULT_HUE,
                    opacity: 0.15 + (d.mean_level / 4) * 0.85,
                  }}
                />
                <span className="heat__id">{d.domain_id}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {/* ---- Eroding ------------------------------------------------------ */}
      {data.eroding_topics.length > 0 ? (
        <div className="card">
          <div className="card__title"><span>Eroding</span></div>
          <ul className="unitlist">
            {data.eroding_topics.map((t) => (
              <li key={t.topic_id}>
                <button type="button" className="unitlist__btn" onClick={() => onOpenTopic(t.topic_id)}>
                  <span className="unitlist__type">Refresh</span>
                  <span className="unitlist__title">{t.title}</span>
                  <span className="unitlist__meta">
                    {t.mean_retrievability !== null
                      ? `${Math.round(t.mean_retrievability * 100)}% recall`
                      : 'stale'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <p className="hint">
            Eroding is a flag and a rotation cue — never a demotion. Mastery and
            Altitude never go down.
          </p>
        </div>
      ) : null}

      {/* ---- Rhythm: deliberately small ---------------------------------- */}
      <p className="rhythm rhythm--small">
        {data.streak.current_days > 0 ? `${data.streak.current_days}-day rhythm` : 'No streak yet'}
        {' · '}{data.streak.week_active_days}/{data.streak.week_goal} this week
      </p>
    </main>
  );
}
